import React, { useState, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@mui/styles';
import {
  Container, Grid, TextField, Button, IconButton, Switch, FormControlLabel,
  Typography, CircularProgress, Paper, Divider, Box,
  Accordion, AccordionSummary, AccordionDetails, Chip,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import { useIntl } from 'react-intl';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import DeleteIcon from '@mui/icons-material/Delete';
import TaskTemplateEditor from '../../components/task-template-editor';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import HeaderMenu from '../../components/header-menu';
import Footer from '../../components/footer';
import { Overlay } from '../../styles/global';
import { Styles } from '../../styles/material-styles';
import CustomizedSnackbars from '../../components/material-snackbars';
import OrgSelectModal from '../../components/org-select-modal';
import CategorySelectModal from '../../components/category-select-modal';
import Api from '../../services/api';
import * as Service from '../../services/tasktemplate.service';

const primaryColor = Api != null ? Api.getPrimaryColorTheme() : '#3F51B4';

const chipSx = { background: '#f0f0f0', color: '#555', border: '1px solid #ddd', fontSize: 11, fontWeight: 500 };

const useStyles = makeStyles(Styles);

export default function TaskTemplateRegistration() {
  const classes = useStyles();
  const history = useHistory();
  const { lang } = useParams();
  const intl = useIntl();
  const headerMenu = useSelector(state => state.headerMenu);
  const actionsHeaderMenu = useSelector(state => state.actionsHeaderMenu);

  const [templateName, setTemplateName] = useState('');
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [blockFinishWhenRequired, setBlockFinishWhenRequired] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orgModalOpen, setOrgModalOpen] = useState(false);
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [notification, setNotification] = useState({ open: false, variant: 'success', message: '' });

  useEffect(() => { loadOrganizations(); }, []);

  useEffect(() => {
    if (selectedOrgs.length > 0) {
      setLoadingCategories(true);
      loadCategories(selectedOrgs.map(o => o.id));
    } else {
      setCategories([]);
      setSelectedCategoryIds([]);
    }
  }, [selectedOrgs]);

  const loadOrganizations = async () => {
    setLoadingOrgs(true);
    const result = await Service.getOrganizations();
    if (result.success) setOrganizations(result.data);
    setLoadingOrgs(false);
  };

  const loadCategories = async (orgIds) => {
    const result = await Service.getCategoriesByOrganizations(orgIds);
    if (result.success) {
      setCategories(result.data);
      setSelectedCategoryIds(prev => prev.filter(id => result.data.some(c => c.id === id)));
    }
    setLoadingCategories(false);
  };

  const showNotification = (variant, message) =>
    setNotification({ open: true, variant, message });

  const handleOrgConfirm = (orgs) => {
    setSelectedOrgs(orgs);
    setOrgModalOpen(false);
  };

  const handleCategoryConfirm = (ids) => {
    setSelectedCategoryIds(ids);
    setCatModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!templateName.trim()) { showNotification('error', 'Informe o nome do template'); return; }
    if (selectedOrgs.length === 0) { showNotification('error', 'Selecione ao menos uma organização'); return; }
    if (tasks.length === 0) { showNotification('error', 'Adicione ao menos uma tarefa'); return; }
    if (tasks.some(t => !t.title.trim())) { showNotification('error', 'Todas as tarefas precisam de título'); return; }

    setLoading(true);
    const data = {
      name: templateName.trim(),
      active: true,
      blockFinishWhenRequiredTaskNotCompleted: blockFinishWhenRequired,
      organizationIds: selectedOrgs.map(o => o.id),
      categoryIds: selectedCategoryIds,
      tasks: tasks.map((t, i) => ({
        title: t.title.trim(),
        description: t.description,
        textFieldRequired: t.textFieldRequired,
        attachmentRequired: t.attachmentRequired,
        attachmentMin: t.attachmentRequired ? t.attachmentMin : 1,
        active: t.active,
        instructionOrder: i + 1,
        requiredToFinishCalled: t.requiredToFinishCalled || false,
      })),
    };
    const result = await Service.add(data);
    if (result.success) {
      showNotification('success', 'Template cadastrado com sucesso!');
      setTimeout(() => history.push(`/${lang}/task-templates`), 1500);
    } else {
      showNotification('error', 'Erro ao cadastrar template');
    }
    setLoading(false);
  };

  const categoryById = useMemo(() => {
    const map = new Map();
    categories.forEach(cat => map.set(cat.id, cat));
    return map;
  }, [categories]);

  const groupedCategories = useMemo(() => (
    selectedOrgs
      .map(org => ({
        org,
        cats: selectedCategoryIds.map(id => categoryById.get(id)).filter(c => c?.organizationId === org.id),
      }))
      .filter(g => g.cats.length > 0)
      .sort((a, b) => a.org.name.localeCompare(b.org.name))
  ), [selectedOrgs, selectedCategoryIds, categoryById]);

  return (
    <div className={classes.root}>
      <HeaderMenu />
      <main
        className={clsx(
          classes.content,
          { [classes.contentShift]: headerMenu.open },
          classes.actionsContent,
          { [classes.actionscontentShift]: actionsHeaderMenu.open }
        )}
      >
        <div className={classes.drawerHeader} />
        <Container component="main" maxWidth="md">
          <Grid container sx={{ mb: 2 }}>
            <Grid item>
              <Button variant="outlined" onClick={() => history.push(`/${lang}/task-templates`)}>
                Voltar
              </Button>
            </Grid>
          </Grid>

          <Typography variant="h5" sx={{ mb: 3 }}>Novo Template de Tarefa do Chamado</Typography>

          <form onSubmit={handleSubmit}>
            <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
              <Typography fontWeight="bold" sx={{ mb: 2 }}>Identificação</Typography>
              <TextField
                fullWidth
                variant="outlined"
                label="Nome do template"
                required
                inputProps={{ maxLength: 250 }}
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
              />
            </Paper>

            <Accordion defaultExpanded variant="outlined" sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">Tarefas</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={blockFinishWhenRequired}
                        onChange={e => {
                          const val = e.target.checked;
                          setBlockFinishWhenRequired(val);
                          if (!val) {
                            setTasks(prev => prev.map(t => ({ ...t, requiredToFinishCalled: false })));
                          }
                        }}
                      />
                    }
                    label={intl.formatMessage({ id: 'task.template.block.finish.required' })}
                  />
                </Grid>
                <TaskTemplateEditor tasks={tasks} onTasksChange={setTasks} blockFinishWhenRequired={blockFinishWhenRequired} />
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded variant="outlined" sx={{ mb: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">Vinculações</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <BusinessIcon fontSize="small" sx={{ color: '#888' }} /> Organizações
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setOrgModalOpen(true)}
                        disabled={loadingOrgs}
                        startIcon={loadingOrgs ? <CircularProgress size={14} /> : null}
                      >
                        {selectedOrgs.length === 0 ? 'Selecionar organizações' : 'Alterar'}
                      </Button>
                    </Box>
                    {selectedOrgs.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                        Nenhuma organização selecionada
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selectedOrgs.map(org => (
                          <Chip
                            key={org.id}
                            label={org.name}
                            size="small"
                            onDelete={() => setSelectedOrgs(prev => prev.filter(o => o.id !== org.id))}
                            sx={chipSx}
                          />
                        ))}
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12}><Divider /></Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CategoryIcon fontSize="small" sx={{ color: '#888' }} /> Categorias
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setCatModalOpen(true)}
                        disabled={selectedOrgs.length === 0 || loadingCategories}
                        startIcon={loadingCategories ? <CircularProgress size={14} /> : null}
                        title={selectedOrgs.length === 0 ? 'Selecione organizações primeiro' : ''}
                      >
                        {selectedCategoryIds.length === 0 ? 'Selecionar categorias' : 'Alterar'}
                      </Button>
                    </Box>
                    {selectedOrgs.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                        Selecione organizações para habilitar a seleção de categorias
                      </Typography>
                    ) : selectedCategoryIds.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                        Nenhuma categoria selecionada (o template se aplicará a todas)
                      </Typography>
                    ) : (
                      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                        {groupedCategories.map(({ org, cats }) => (
                          <Accordion key={org.id} defaultExpanded variant="outlined" disableGutters
                            sx={{ '&:before': { display: 'none' }, borderLeft: 'none', borderRight: 'none', borderTop: 'none', borderRadius: '0 !important' }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 38, '& .MuiAccordionSummary-content': { alignItems: 'center', my: 0 } }}>
                              <Typography sx={{ flexGrow: 1, fontSize: 12, fontWeight: 700 }}>
                                {org.name}
                                <Box component="span" sx={{ color: '#999', fontWeight: 400, ml: 0.5 }}>({cats.length})</Box>
                              </Typography>
                              <Button
                                size="small"
                                color="error"
                                sx={{ fontSize: 11, minWidth: 0, mr: 1 }}
                                onClick={e => { e.stopPropagation(); setSelectedCategoryIds(prev => prev.filter(id => categoryById.get(id)?.organizationId !== org.id)); }}
                              >
                                Remover todos
                              </Button>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', py: 1 }}>Nome</TableCell>
                                    <TableCell sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', py: 1 }}>Hierarquia</TableCell>
                                    <TableCell sx={{ py: 1, width: 44 }} />
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {cats.map(cat => (
                                    <TableRow key={cat.id} hover>
                                      <TableCell sx={{ fontSize: 13, py: 0.75 }}>{cat.name}</TableCell>
                                      <TableCell sx={{ fontSize: 12, color: 'text.secondary', py: 0.75 }}>{cat.parentname || cat.name}</TableCell>
                                      <TableCell sx={{ py: 0.75, pr: 0.5 }}>
                                        <IconButton
                                          size="small"
                                          onClick={() => setSelectedCategoryIds(prev => prev.filter(i => i !== cat.id))}
                                          sx={{ color: '#bbb', '&:hover': { color: 'error.main' } }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Grid container justifyContent="flex-end">
              <Grid item xs={12} sm={4}>
                <Button type="submit" fullWidth variant="contained" color="primary">
                  Salvar template
                </Button>
              </Grid>
            </Grid>
          </form>
        </Container>
      </main>

      {loading && <Overlay><CircularProgress color="secondary" /></Overlay>}

      <OrgSelectModal
        open={orgModalOpen}
        onClose={() => setOrgModalOpen(false)}
        onConfirm={handleOrgConfirm}
        organizations={organizations}
        loading={loadingOrgs}
        initialSelected={selectedOrgs}
      />

      <CategorySelectModal
        open={catModalOpen}
        onClose={() => setCatModalOpen(false)}
        onConfirm={handleCategoryConfirm}
        selectedOrgs={selectedOrgs}
        categories={categories}
        initialSelectedIds={selectedCategoryIds}
      />

      <CustomizedSnackbars
        variant={notification.variant}
        message={notification.message}
        isOpen={notification.open}
        toClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
      <Footer />
    </div>
  );
}
